import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'websites', name: 'Websites' },
    { id: 'fintech', name: 'Fintech' },
    { id: 'apps', name: 'Apps' },
  ];

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(p => p.category === activeCategory);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section
        data-testid="projects-hero"
        className="relative py-24 lg:py-32 hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771011726574-60ab6028724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NjI5Mzg0fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-6">
              Our Portfolio
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Projects That Make an Impact
            </h1>
            <p className="text-lg text-[#A1B0CC] leading-relaxed">
              Explore our portfolio of successful projects across various industries, 
              from e-commerce platforms to fintech solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0A1121] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                data-testid={`project-filter-${category.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === category.id
                    ? 'bg-[#FF5E1A] text-white'
                    : 'bg-[#050B14] text-[#A1B0CC] hover:text-white border border-white/10'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section data-testid="projects-grid" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                data-testid={`project-card-${index}`}
                className="group bg-[#0A1121] border border-white/5 rounded-2xl overflow-hidden card-hover"
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1121] via-transparent to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="tech-badge text-xs px-3 py-1 rounded-full capitalize">
                      {project.category}
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-6">
                  <h3 className="text-white font-bold text-xl mb-2 group-hover:text-[#FF5E1A] transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-[#A1B0CC] text-sm leading-relaxed mb-4">
                    {project.description}
                  </p>

                  {/* Technologies */}
                  {project.technologies && project.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-1 bg-[#050B14] text-[#A1B0CC] rounded border border-white/5"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}

                  <button className="flex items-center gap-2 text-[#FF5E1A] text-sm font-medium hover:gap-3 transition-all">
                    View Details <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#A1B0CC]">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-[#0A1121]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '50+', label: 'Projects Completed' },
              { value: '30+', label: 'Happy Clients' },
              { value: '10+', label: 'Industries Served' },
              { value: '5+', label: 'Years Experience' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-[#FF5E1A] mb-2">
                  {stat.value}
                </div>
                <div className="text-[#A1B0CC] text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#FF5E1A]/20 to-transparent border border-[#FF5E1A]/20 rounded-3xl p-12 lg:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Have a Project in Mind?
            </h2>
            <p className="text-[#A1B0CC] max-w-2xl mx-auto mb-8">
              Let&apos;s turn your ideas into reality. Get in touch with us to discuss 
              your next project.
            </p>
            <Link
              to="/contact"
              data-testid="projects-cta"
              className="inline-flex items-center gap-2 bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full px-8 py-4 transition-all btn-glow"
            >
              Start Your Project <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
